import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  UserPlus,
  RefreshCw,
  Trash2,
  Pencil,
  Loader2,
  Shield,
  LogIn,
  ExternalLink,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const VEDI_TUTTI = "commerciale.vedi_tutti";

const emptyForm = {
  email: "",
  name: "",
  password: "",
  profile: "contenuti_editor",
  active: true,
  vediTutti: false,
  originalProfile: null,
  basePermissions: null,
};

/** Permessi da inviare quando cambia «vede tutte le visite» (null = nessun override da inviare). */
export function permissionsForSave(form, profiles) {
  const profileChanged = form.originalProfile !== null && form.profile !== form.originalProfile;
  const profilePerms = (profiles.find((p) => p.id === form.profile) || {}).permissions || [];
  const base = form.originalProfile === null || profileChanged ? profilePerms : (form.basePermissions || profilePerms);
  if (base.includes(VEDI_TUTTI) === !!form.vediTutti) return null;
  return form.vediTutti ? [...base, VEDI_TUTTI] : base.filter((p) => p !== VEDI_TUTTI);
}

export default function AdminLimitedUsersModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, profilesRes] = await Promise.all([
        fetch(`${API}/api/admin/crm/users`, { headers }),
        fetch(`${API}/api/admin/crm/profiles`, { headers }),
      ]);
      if (!usersRes.ok) throw new Error("Errore caricamento utenti");
      const uj = await usersRes.json();
      setUsers(uj.data || []);
      if (profilesRes.ok) {
        const pj = await profilesRes.json();
        setProfiles(pj.data || []);
      }
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditId(u.id);
    setForm({
      email: u.email,
      name: u.name || "",
      password: "",
      profile: u.profile || "crm_operatore",
      active: !!u.active,
      vediTutti: (u.permissions || []).includes(VEDI_TUTTI),
      originalProfile: u.profile || "crm_operatore",
      basePermissions: u.permissions || null,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!editId) {
      if (!form.email.trim() || !form.password) {
        toast.error("Email e password obbligatori");
        return;
      }
    }
    setSaving(true);
    try {
      if (editId) {
        const body = {
          name: form.name,
          active: form.active,
        };
        // Il profilo si invia solo se cambia: reinviarlo azzererebbe i permessi personalizzati.
        if (form.profile !== form.originalProfile) body.profile = form.profile;
        const permissions = permissionsForSave(form, profiles);
        if (permissions) body.permissions = permissions;
        if (form.password) body.password = form.password;
        const res = await fetch(`${API}/api/admin/crm/users/${editId}`, {
          method: "PATCH",
          headers,
          body: JSON.stringify(body),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.detail || "Errore aggiornamento");
        toast.success("Utente aggiornato");
      } else {
        const res = await fetch(`${API}/api/admin/crm/users`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            email: form.email.trim(),
            password: form.password,
            name: form.name,
            active: form.active,
            profile: form.profile,
            ...(permissionsForSave(form, profiles) ? { permissions: permissionsForSave(form, profiles) } : {}),
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.detail || "Errore creazione");
        toast.success("Utente admin limitato creato");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(typeof e.message === "string" ? e.message : "Errore");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Eliminare l'utente ${email}?`)) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${API}/api/admin/crm/users/${id}`, {
        method: "DELETE",
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore eliminazione");
      toast.success("Utente eliminato");
      load();
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setDeletingId(null);
    }
  };

  const impersonate = async (u) => {
    if (!u?.id || !u.active) return;
    setImpersonatingId(u.id);
    try {
      const res = await fetch(`${API}/api/admin/crm/users/${u.id}/impersonate`, {
        method: "POST",
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore impersonazione");
      if (!json.access_token) throw new Error("Token non ricevuto");
      const url = new URL(window.location.origin);
      url.pathname = "/admin";
      url.searchParams.set("admin_impersonate_token", json.access_token);
      window.open(url.toString(), "_blank");
      toast.success(`Login come ${u.name || u.email} aperto in nuova scheda`);
    } catch (e) {
      toast.error(e.message || "Errore durante l'accesso");
    } finally {
      setImpersonatingId(null);
    }
  };

  const selectedProfile = profiles.find((p) => p.id === form.profile);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="h-5 w-5 text-rose-600" />
            Utenti admin limitati
          </h2>
          <p className="text-sm text-slate-500 max-w-2xl">
            Crea account che accedono al pannello Admin (/admin-login) con menu e azioni
            ristretti. I super admin completi restano quelli definiti nelle variabili
            d&apos;ambiente.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
          <Button onClick={openCreate} size="sm">
            <UserPlus className="h-4 w-4 mr-2" />
            Nuovo utente
          </Button>
        </div>
      </div>

      {profiles.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {profiles.map((p) => (
            <Card key={p.id} className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{p.label}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600 space-y-2">
                <p>{p.description}</p>
                <p className="text-xs text-slate-400">
                  Moduli: {(p.modules || []).join(", ")}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Utente
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Profilo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Stato
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase w-48">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                      <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                      Caricamento…
                    </td>
                  </tr>
                ) : !users.length ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                      Nessun utente limitato. Creane uno con «Nuovo utente».
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{u.email}</div>
                        {u.name ? (
                          <div className="text-xs text-slate-500">{u.name}</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">{u.profile_label || u.profile}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={u.active ? "default" : "secondary"}>
                          {u.active ? "Attivo" : "Disattivo"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          {u.active ? (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={impersonatingId === u.id}
                              onClick={() => impersonate(u)}
                              className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
                              title="Apri il pannello Admin come questo utente"
                            >
                              {impersonatingId === u.id ? (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <LogIn className="h-4 w-4 mr-1" />
                              )}
                              Login
                              <ExternalLink className="h-3 w-3 ml-1 opacity-60" />
                            </Button>
                          ) : null}
                          <Button variant="ghost" size="icon" onClick={() => openEdit(u)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600"
                            disabled={deletingId === u.id}
                            onClick={() => remove(u.id, u.email)}
                          >
                            {deletingId === u.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? "Modifica utente" : "Nuovo utente admin limitato"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Email</Label>
              <Input
                value={form.email}
                disabled={!!editId}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="nome@esempio.it"
              />
            </div>
            <div>
              <Label>Nome</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>{editId ? "Nuova password (opzionale)" : "Password"}</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              />
            </div>
            <div>
              <Label>Profilo</Label>
              <Select
                value={form.profile}
                onValueChange={(v) => setForm((p) => ({ ...p, profile: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedProfile && (
                <p className="text-xs text-slate-500 mt-1">{selectedProfile.description}</p>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm((p) => ({ ...p, active: !!v }))}
              />
              Account attivo
            </label>
            <label className="flex items-start gap-2 text-sm">
              <Switch
                checked={!!form.vediTutti}
                onCheckedChange={(v) => setForm((p) => ({ ...p, vediTutti: !!v }))}
                aria-label="Vede tutte le visite commerciali"
              />
              <span>
                Vede tutte le visite commerciali
                <span className="block text-xs text-slate-500">
                  Sola lettura sulle visite degli altri commerciali (serve il modulo «Le mie visite»).
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Annulla
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editId ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
